import { useState, useCallback, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faNoteSticky,
  faPenToSquare,
  faCheck,
} from '@fortawesome/pro-regular-svg-icons';
import { useActionExecution } from '../../hooks/useActionExecution';
import { askMoo } from '../../../../queries/ask-moo';
import type { FormNoteDraftAction, AssistantMessage } from '../../types';
import styles from './NoteDraftCard.module.css';
import useNotifications from '../../../../_lib/notifications/useNotifications';
import NoteCandidateOption from './NoteCandidateOption';

interface Props {
  readonly action: FormNoteDraftAction;
  readonly message: AssistantMessage;
  readonly isLastMessage?: boolean;
}

export default function NoteDraftCard({
  action,
  message,
  isLastMessage,
}: Props) {
  const notifications = useNotifications();
  const { mutateAsync, isLoading } = useActionExecution();
  const queryClient = useQueryClient();

  const defaultIndex = Math.max(
    action.candidates.findIndex(
      (c) => c.entityType === action.defaultEntityType
    ),
    0
  );

  // Track the selection by index rather than entity id so the id is never
  // written into the <select>/<option> markup (OMNI-634).
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex);
  const selected = action.candidates[selectedIndex];
  const [saved, setSaved] = useState(action.executed ?? false);
  const [title, setTitle] = useState(action.title);
  const [noteText, setNoteText] = useState(action.noteText);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isDisabled = saved || !isLastMessage || isLoading;

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [noteText]);

  const handleSave = useCallback(async () => {
    if (!selected || !isLastMessage) return;
    try {
      const result = await mutateAsync({
        correlationId: message.correlationId,
        messageId: message.messageId,
        action: action.confirmAction,
        payload: {
          entityType: selected.entityType,
          entityId: selected.entityId,
          title,
          noteText,
        },
      });
      if (!result?.success) {
        notifications.error(
          result?.result?.message ?? 'Could not save note. Please try again.'
        );
        return;
      }
      setSaved(true);
      queryClient.invalidateQueries({
        queryKey: askMoo.conversation._def,
        refetchType: 'none',
      });
    } catch {
      notifications.error('Could not save note. Please try again.');
    }
  }, [
    selected,
    isLastMessage,
    mutateAsync,
    message,
    action,
    title,
    noteText,
    notifications,
    queryClient,
  ]);

  return (
    <div className={styles.noteCard}>
      {/* Header */}
      <div className={styles.noteHeader}>
        <div className={styles.noteHeaderLeft}>
          <FontAwesomeIcon icon={faNoteSticky} style={{ fontSize: 14 }} />
          Note Draft
        </div>
        {saved && (
          <div className={styles.noteSavedBadge}>
            <FontAwesomeIcon icon={faCheck} style={{ fontSize: 11 }} />
            Note saved
          </div>
        )}
      </div>

      {/* Entity selector */}
      <div className={styles.noteField}>
        <span className={styles.noteFieldLabel}>Save to:</span>
        <select
          className={styles.noteSelect}
          value={String(selectedIndex)}
          onChange={(e) => setSelectedIndex(Number(e.target.value))}
          disabled={isDisabled}
        >
          {action.candidates.map((c, i) => (
            <NoteCandidateOption
              key={c.entityId}
              candidate={c}
              value={String(i)}
            />
          ))}
        </select>
      </div>

      <hr className={styles.noteDivider} />

      {/* Editable note content */}
      <input
        className={styles.noteTitleInput}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        readOnly={isDisabled}
        disabled={isDisabled}
      />
      <textarea
        ref={textareaRef}
        className={styles.noteBodyTextarea}
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
        readOnly={isDisabled}
        disabled={isDisabled}
      />

      {/* Action button */}
      <div className={styles.noteActions}>
        <button
          className={
            isDisabled ? styles.noteSaveButtonDisabled : styles.noteSaveButton
          }
          onClick={handleSave}
          disabled={isDisabled}
          type="button"
        >
          <FontAwesomeIcon icon={faPenToSquare} style={{ fontSize: 12 }} />
          {isLoading ? 'Saving…' : 'Add Note'}
        </button>
      </div>
    </div>
  );
}
